// Derive a full toon CharacterPalette from a single base hex color.
// The hand-tuned player/AI palettes layer a cold shadow, a desaturated mid, a
// bright high and an even brighter rim. We mimic that in HSL so any base color
// produces a consistent "character" look instead of a flat tint.

import type { CharacterPalette } from '../render/CharacterView';

type RGB = [number, number, number];
type HSL = [number, number, number];

function hexToRgb(hex: string): RGB {
  const h = hex.replace('#', '');
  const v = h.length === 3
    ? h.split('').map((c) => parseInt(c + c, 16))
    : [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  return [v[0]! / 255, v[1]! / 255, v[2]! / 255];
}

function rgbToHex([r, g, b]: RGB): string {
  const to = (x: number) => Math.round(Math.max(0, Math.min(1, x)) * 255).toString(16).padStart(2, '0');
  return '#' + to(r) + to(g) + to(b);
}

function rgbToHsl([r, g, b]: RGB): HSL {
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  switch (max) {
    case r: h = ((g - b) / d + (g < b ? 6 : 0)); break;
    case g: h = ((b - r) / d + 2); break;
    default: h = ((r - g) / d + 4); break;
  }
  return [h / 6, s, l];
}

function hslToRgb([h, s, l]: HSL): RGB {
  if (s === 0) return [l, l, l];
  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue2rgb(p, q, h + 1 / 3), hue2rgb(p, q, h), hue2rgb(p, q, h - 1 / 3)];
}

function shiftHex(base: string, dH: number, sMul: number, dL: number): string {
  const [h, s, l] = rgbToHsl(hexToRgb(base));
  const h2 = (h + dH + 1) % 1;
  const s2 = Math.max(0, Math.min(1, s * sMul));
  const l2 = Math.max(0, Math.min(1, l + dL));
  return rgbToHex(hslToRgb([h2, s2, l2]));
}

/**
 * Build a CharacterPalette from a single base hex. Shifts are tuned to echo
 * the hand-tuned palettes in CharacterView — cold purple-ish shadow, peachy
 * mid, cream high, pale rim, near-black outline.
 */
export function paletteFromBase(baseHex: string): CharacterPalette {
  return {
    base: baseHex,
    shadow: shiftHex(baseHex, +0.55, 0.9, -0.40), // complementary-shifted, dark
    mid:    shiftHex(baseHex, -0.02, 0.7, -0.05),
    high:   shiftHex(baseHex, +0.00, 0.55, +0.22),
    rim:    shiftHex(baseHex, +0.02, 0.4, +0.28),
    outline: '#0d0816'
  };
}

/**
 * Derive the three colors used by the "YOU" badge (body base, gradient top
 * highlight, outline+text stroke) from the player's chosen color. Caps
 * lightness so near-white picks land on a mid-gray badge rather than washing
 * out the palette — the cap scales with saturation so saturated hues
 * (orange, blue, etc.) pass through at their picked lightness while gray/
 * white are pulled down to ~40% gray.
 */
export function badgeColorsFromBase(baseHex: string): { baseColor: string; highlightColor: string; strokeColor: string } {
  const [h, s, l] = rgbToHsl(hexToRgb(baseHex));
  const maxL = 0.4 + 0.15 * Math.min(1, s);
  const cappedL = Math.min(maxL, l);
  const baseColor = rgbToHex(hslToRgb([h, s, cappedL]));
  const highlightColor = rgbToHex(hslToRgb([h, Math.min(1, s * 0.95), Math.min(0.72, cappedL + 0.16)]));
  const strokeColor = rgbToHex(hslToRgb([h, s * 0.6, Math.max(0.08, cappedL - 0.3)]));
  return { baseColor, highlightColor, strokeColor };
}
